/**
 * scenario-structured.ts — champs d'AFFICHAGE du scénario (30/07/2026)
 *
 * L'onglet « Scénario » est rendu en 4 sections (Ce que tu vends / Ton interlocuteur /
 * L'entreprise du prospect / Contexte connu avant l'appel) au lieu d'un unique bloc de
 * texte qui répétait tout. Deux de ces informations ne peuvent venir que de N8N :
 *   - `prospectCompanyDescription` : la description de l'entreprise qu'il a inventée ;
 *   - `selectedIndustry` : le secteur qu'il a retenu quand le cas en propose plusieurs.
 *
 * ⚠️ Ces champs ne remplacent PAS `userMessage`, qui reste le brief de l'agent vocal, la
 * source du feedback, de l'historique et de l'endpoint pull consommé par N8N.
 *
 * Tout est TOLÉRANT : tant que le workflow N8N n'est pas migré (et pour tous les scénarios
 * déjà en base), ces fonctions renvoient des chaînes vides et l'affichage retombe sur
 * l'ancien rendu. Aucun plantage possible.
 */
import { looksLikeScenarioRefusal } from "./scenario-refusal";

/** Longueurs maximales stockées (garde-fou : un LLM peut partir en digression). */
export const MAX_LEN = {
  prospectCompanyDescription: 600,
  selectedIndustry: 200,
  priorContext: 4000,
} as const;

/**
 * Nettoie un texte renvoyé par N8N : type, espaces, refus, longueur.
 * Un refus (« Je ne peux pas… ») est traité comme une absence de valeur — mieux vaut une
 * section incomplète qu'une excuse de LLM affichée à l'apprenant.
 */
export function sanitizeScenarioText(value: unknown, maxLen: number): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (looksLikeScenarioRefusal(trimmed)) return "";
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen).trimEnd() : trimmed;
}

/** Découpe une liste de secteurs, qu'elle soit en JSON (`["a","b"]`) ou jointe (`a, b`). */
function splitIndustryList(list: string | null | undefined): string[] {
  if (!list) return [];
  try {
    const parsed = JSON.parse(list);
    if (Array.isArray(parsed)) return parsed.map((x) => String(x).trim()).filter(Boolean);
  } catch {
    /* pas du JSON : on retombe sur le découpage par virgule */
  }
  return list.split(",").map((x) => x.trim()).filter(Boolean);
}

/** Clé de comparaison tolérante : casse, accents et espaces multiples neutralisés. */
function comparisonKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Résout le secteur retenu par N8N vers la valeur CANONIQUE de BizCoach.
 *
 * Le contrat demande à N8N une copie conforme d'une des valeurs envoyées, mais un LLM
 * dérape facilement (casse, accent perdu, et surtout traduction quand le reste de la
 * réponse est en anglais). On rattrape ce qui est rattrapable, et on renvoie TOUJOURS la
 * valeur telle que BizCoach la connaît — c'est indispensable pour que `localizeIndustry`
 * retrouve sa clé de dictionnaire. Une valeur non reconnue est abandonnée (`""`) plutôt
 * qu'affichée : sinon l'écran mélangerait français et anglais.
 *
 * @param returned  ce que N8N a renvoyé
 * @param sentList  la liste EXACTE envoyée dans le payload (après expandAllIndustriesMarker)
 */
export function normalizeSelectedIndustry(
  returned: string | null | undefined,
  sentList: string | null | undefined,
): string {
  const candidates = splitIndustryList(sentList);
  if (candidates.length === 0) return "";

  // Un seul secteur possible : N8N n'avait pas de choix à faire, BizCoach sait mieux.
  if (candidates.length === 1) return candidates[0]!;

  const wanted = typeof returned === "string" ? comparisonKey(returned) : "";
  if (!wanted) return "";

  const match = candidates.find((c) => comparisonKey(c) === wanted);
  return match || "";
}

export type StructuredScenarioFields = {
  prospectCompanyDescription: string;
  selectedIndustry: string;
  /** Contexte d'avant-appel renvoyé par N8N. Sert UNIQUEMENT de repli : quand le cas de
   *  simulation porte un contexte, BizCoach affiche SON brief joueur, qui fait foi.
   *  Mais le contexte est optionnel à la création d'un cas, et dans ce cas N8N en tire un
   *  au sort dans sa propre liste par simulateur — sans ce repli, l'apprenant jouerait un
   *  appel dont il ignore la situation de départ. */
  priorContext: string;
};

/**
 * Extrait les champs structurés d'une réponse `scenarioAgent`, quelle que soit sa forme
 * (objet, tableau à un élément, null, payload legacy sans ces champs).
 */
export function extractStructuredScenario(
  data: unknown,
  sentIndustryList: string | null | undefined,
): StructuredScenarioFields {
  const node = Array.isArray(data) ? data[0] : data;
  const obj = (node && typeof node === "object" ? node : {}) as Record<string, unknown>;

  return {
    prospectCompanyDescription: sanitizeScenarioText(
      obj.prospectCompanyDescription,
      MAX_LEN.prospectCompanyDescription,
    ),
    selectedIndustry: normalizeSelectedIndustry(
      sanitizeScenarioText(obj.selectedIndustry, MAX_LEN.selectedIndustry),
      sentIndustryList,
    ),
    priorContext: sanitizeScenarioText(obj.priorContext, MAX_LEN.priorContext),
  };
}

/**
 * Textes d'affichage du brief, rédigés par N8N dans la LANGUE D'ENTRAÎNEMENT (21/09/2026).
 *
 * Ces six informations viennent de la base (cas, offre, persona, style), qui ne les connaît
 * qu'en français et en anglais. Pour un collaborateur qui s'entraîne en russe, seul N8N peut
 * les écrire en russe : il les a déjà sous les yeux (payload d'entrée) et rédige le scénario
 * dans cette langue. Contrat : `spec/brief-mariam-n8n-brief-langue-entrainement.md`.
 *
 * Tous les champs sont OPTIONNELS, un par un : un champ absent retombe sur la valeur de la
 * base, dans la langue d'interface. Une section à moitié traduite vaut mieux qu'une section vide.
 */
export type SpokenBrief = {
  offerLabel?: string;
  offerDescription?: string;
  /** Fonction en B2B, « CSP · âge » en B2C. */
  interlocutorSubtitle?: string;
  /** Personnalité jouée, sur ses deux niveaux (« Dominant pragmatique »). */
  personalityStyle?: string;
  industryLabel?: string;
  companySizeLabel?: string;
};

/** Les mêmes six champs, tels que la base les fournit (tous présents, parfois vides). */
export type SpokenBriefBase = Record<keyof SpokenBrief, string | undefined>;

const SPOKEN_BRIEF_MAX_LEN: Record<keyof SpokenBrief, number> = {
  offerLabel: 200,
  offerDescription: 1000,
  interlocutorSubtitle: 200,
  personalityStyle: 200,
  industryLabel: 200,
  companySizeLabel: 100,
};

/**
 * Extrait l'objet `briefSpoken` d'une réponse `scenarioAgent`. Renvoie `null` quand il est
 * absent ou entièrement vide — c'est le cas de tout scénario généré avant que le workflow
 * ne le renvoie, et l'affichage retombe alors sur l'ancien rendu.
 */
export function extractSpokenBrief(data: unknown): SpokenBrief | null {
  const node = Array.isArray(data) ? data[0] : data;
  const obj = (node && typeof node === "object" ? node : {}) as Record<string, unknown>;
  const raw = obj.briefSpoken;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const out: SpokenBrief = {};
  for (const key of Object.keys(SPOKEN_BRIEF_MAX_LEN) as Array<keyof SpokenBrief>) {
    const value = sanitizeScenarioText((raw as Record<string, unknown>)[key], SPOKEN_BRIEF_MAX_LEN[key]);
    if (value) out[key] = value;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Superpose les textes rédigés par N8N dans la langue d'entraînement aux valeurs de la base.
 *
 * Champ par champ : ce que N8N n'a pas renvoyé garde la valeur de la base (langue
 * d'interface). Partagé par l'écran d'entraînement et l'historique — la règle ne doit
 * exister qu'une fois.
 */
export function applySpokenBrief(base: SpokenBriefBase, spoken: SpokenBrief | null | undefined): SpokenBriefBase {
  if (!spoken) return base;
  const out = { ...base };
  for (const key of Object.keys(SPOKEN_BRIEF_MAX_LEN) as Array<keyof SpokenBrief>) {
    const value = spoken[key];
    if (typeof value === "string" && value.trim()) (out as SpokenBrief)[key] = value;
  }
  return out;
}

/**
 * Faut-il afficher le brief dans la langue d'entraînement plutôt que dans celle de l'interface ?
 *
 * Oui dès que la séance a été générée avec une langue d'entraînement DIFFÉRENTE de la langue
 * d'interface d'alors. On ne compare PAS `spokenLanguage` à la langue d'interface du LECTEUR :
 * le récit de N8N (nom du prospect, description de l'entreprise, contexte) est rédigé une fois
 * pour toutes dans la langue d'entraînement, et un habillage dans une autre langue rendrait
 * l'écran bilingue — exactement le défaut corrigé ici.
 *
 * @param spokenLanguage `scenarios.spoken_language` (NULL = langues identiques à la génération)
 * @param userLanguage   langue d'interface à la génération
 */
export function spokenLanguageForBrief(
  spokenLanguage: string | null | undefined,
  userLanguage: string,
): string | null {
  if (!spokenLanguage) return null;
  return spokenLanguage !== userLanguage ? spokenLanguage : null;
}

/**
 * Le scénario porte-t-il les données du rendu en 4 sections ?
 *
 * ⚠️ Ne PAS se baser sur `priorContext` seul : un cas créé sans contexte (« Aucun
 * contexte » est un choix valide) en est légitimement dépourvu, et basculerait à tort en
 * affichage legacy. `prospectCompanyDescription` sert donc de second témoin.
 */
export function hasStructuredScenario(
  scenario: { priorContext?: string | null; prospectCompanyDescription?: string | null } | null | undefined,
): boolean {
  if (!scenario) return false;
  return Boolean(
    (scenario.priorContext && scenario.priorContext.trim()) ||
      (scenario.prospectCompanyDescription && scenario.prospectCompanyDescription.trim()),
  );
}

/**
 * Compose les DEUX niveaux de la personnalité en un seul libellé, ex. « Dominant pragmatique ».
 *
 * Le style (niveau 2) n'a de sens que rattaché à sa famille DISC (niveau 1) : « Pragmatique »
 * appartient à « Dominant », et un même style se lirait autrement dans une autre famille.
 * L'apprenant doit voir les deux pour anticiper le comportement de l'agent.
 *
 * Seule la première lettre du style est abaissée (et non tout le libellé) pour ne pas
 * abîmer un style contenant une majuscule interne ou un sigle.
 */
export function formatPersonalityPair(
  category: string | null | undefined,
  style: string | null | undefined,
): string {
  const cat = (category || "").trim();
  const sty = (style || "").trim();
  if (!cat && !sty) return "";
  if (!cat) return sty;
  if (!sty) return cat;
  return `${cat} ${sty.charAt(0).toLowerCase()}${sty.slice(1)}`;
}

/**
 * Aplatit `scenarios.variables` (JSON `{ clé: { type, text } }`) en objet plat.
 *
 * C'est l'INSTANTANÉ des données au moment de la génération — la bonne source pour
 * l'historique : supprimer un cas de simulation est un vrai DELETE, et le relire pour
 * afficher une session passée la viderait rétroactivement.
 */
export function parseScenarioVariables(variablesJson: string | null | undefined): Record<string, string> {
  if (!variablesJson) return {};
  try {
    const parsed = JSON.parse(variablesJson);
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "string") out[key] = value;
      else if (value && typeof value === "object" && typeof (value as any).text === "string") {
        out[key] = (value as any).text;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Reconstitue le brief d'une session PASSÉE à partir du seul scénario (colonnes + snapshot
 * `variables`), sans aucune dépendance au cas de simulation — qui a pu être modifié ou
 * supprimé depuis.
 *
 * Pièges de `variables`, tous rencontrés en vrai :
 *  - la fonction de l'interlocuteur y est sous la clé `prospect_type`, et n'est écrite
 *    QU'EN B2B → en B2C on n'affiche pas de sous-titre plutôt qu'un vide ;
 *  - `industry` est une chaîne déjà jointe, pas le JSON de `targets.industry` ;
 *  - `offer_description` est concaténé `"Nom: description"` (multi-offres séparées par \n).
 */
export function buildBriefFromVariables(
  scenario: {
    variables?: string | null;
    selectedIndustry?: string | null;
  } | null | undefined,
): {
  offerLabel: string;
  offerDescription: string;
  interlocutorSubtitle: string;
  /** Personnalité RÉELLEMENT jouée, sur ses DEUX niveaux : la famille DISC puis le style,
   *  ex. « Dominant pragmatique ». Les deux comptent — un même style se lit différemment
   *  selon sa famille, et l'apprenant a besoin des deux pour anticiper le comportement.
   *  ⚠️ Lu ici et pas sur le persona : en mode « variable », BizCoach fait tourner les
   *  styles à chaque session, le persona ne dit donc pas lequel a été joué. */
  personalityStyle: string;
  industryRaw: string;
  companySize: string;
  isB2C: boolean;
} {
  const v = parseScenarioVariables(scenario?.variables);
  const isB2C = (v.case_type || "").toUpperCase() === "B2C";

  // "Nom: description" → on ne garde que la description pour la ligne du dessous.
  const rawOfferDescription = v.offer_description || "";
  const firstOffer = rawOfferDescription.split("\n")[0] || "";
  const sep = firstOffer.indexOf(":");
  const offerDescription = sep >= 0 ? firstOffer.slice(sep + 1).trim() : firstOffer.trim();

  const subtitle = isB2C
    ? [v.csp, v.age_range].filter(Boolean).join(" · ")
    : v.prospect_type || "";

  return {
    offerLabel: v.offer_name || "",
    offerDescription,
    interlocutorSubtitle: subtitle,
    personalityStyle: formatPersonalityPair(v.personality_category, v.personality_style),
    industryRaw: pickDisplayIndustry(scenario?.selectedIndustry, v.industry),
    companySize: v.company_size || "",
    isB2C,
  };
}

/**
 * Secteur à afficher dans la section « L'entreprise du prospect ».
 *
 * Ordre : le choix de N8N → à défaut, le secteur du cas s'il est unique → sinon RIEN.
 * Ne jamais afficher la liste complète des secteurs du cas : un prospect appartient à UN
 * secteur, en lister douze est faux (c'est le bug d'origine de ce chantier).
 *
 * @returns la valeur BRUTE (français), à passer à localizeIndustry() par l'appelant.
 */
export function pickDisplayIndustry(
  scenarioSelectedIndustry: string | null | undefined,
  targetIndustry: string | null | undefined,
): string {
  const chosen = (scenarioSelectedIndustry || "").trim();
  if (chosen) return chosen;

  const caseIndustries = splitIndustryList(targetIndustry);
  return caseIndustries.length === 1 ? caseIndustries[0]! : "";
}
